import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CaretUp, CaretDown, FileVideo, CheckCircle, Circle } from '@phosphor-icons/react';

import type { UploadState } from './types';
import { formatFileSize } from './utils';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

export interface ProcessingStep {
  id: string;
  label: string;
  status: 'completed' | 'current' | 'pending' | 'error';
}

interface FileUploadStatusProps {
  uploads: UploadState[];
  className?: string;
  timeRemaining?: string;
  onCancelUpload?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  steps?: ProcessingStep[];
  title?: string;
}

const CheckCircleIcon = React.memo<{ className?: string }>(({ className }) => {
  return (
    <div className={cn('relative', className)}>
      <CheckCircle className="absolute size-6 text-green-400" weight="fill" />
      <CheckCircle className="absolute size-6 text-black" weight="regular" />
    </div>
  );
});

CheckCircleIcon.displayName = 'CheckCircleIcon';

export const FileUploadStatus: React.FC<FileUploadStatusProps> = ({
  uploads,
  className,
  onCancelUpload,
  steps = [
    { id: 'upload', label: 'Uploading video', status: 'current' },
    { id: 'analyze', label: 'Analyzing content', status: 'pending' },
    { id: 'create', label: 'Creating help articles', status: 'pending' },
  ],
  timeRemaining,
  title = 'Video Processing',
}) => {
  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    setMounted(true);
  }, []);

  const cancelButtonLabel = useCallback((uploadState: UploadState) => {
    if (uploadState.status === 'success') return 'Uploaded';
    if (uploadState.status === 'error') return 'Retry upload';

    return 'Cancel upload';
  }, []);

  if (!mounted || uploads.length === 0) return null;

  // Ensure we're in a browser environment
  if (typeof document === 'undefined') return null;

  const statusCard = (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={cn(
        'fixed bottom-3 right-3 w-96 shadow-lg border z-50 transition-all duration-200 h-auto p-0 gap-4 bg-dark-100 rounded-lg overflow-hidden',
        className,
      )}
    >
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between py-4 px-6 bg-foreground text-background cursor-pointer">
          <div className="flex flex-col gap-0">
            <span className="font-semibold">{title}</span>
            {timeRemaining && <span className="text-sm text-muted-foreground">{timeRemaining}</span>}
          </div>
          <Button variant="ghost" size="sm" className="size-6 p-0">
            {isOpen ? <CaretDown className="size-4" /> : <CaretUp className="size-4" />}
          </Button>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {uploads.map((upload) => (
          <Card key={upload.id} className={cn('rounded-none p-0')}>
            <CardContent className="p-0">
              {/* File Info */}
              <div className="px-6 py-4 border-b">
                <div className="flex items-center gap-4">
                  <div className="size-12 bg-yellow-400 border border-dark rounded-lg flex items-center justify-center text-dark shrink-0">
                    <FileVideo className="size-6" />
                  </div>
                  <div className="flex-1 flex flex-col gap-0">
                    <h3 className="text-base font-medium flex items-center gap-1 whitespace-nowrap">
                      <span className="truncate max-w-56">{upload.file.name}</span>
                      <span className="shrink-0"> • {formatFileSize(upload.file.size)}</span>
                    </h3>
                    <Button
                      variant="dark"
                      size="sm"
                      className="self-start h-6 text-xs bg-foreground text-background"
                      onClick={onCancelUpload}
                      data-id={upload.id}
                      disabled={upload.status === 'success' || upload.progress === 100}
                      data-status={upload.status}
                    >
                      {cancelButtonLabel(upload)}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Steps */}
              <ol className="py-2">
                {steps.map((step) => (
                  <li
                    key={step.id}
                    className={cn(
                      'flex items-center gap-4 px-6 py-3 relative',
                      'after:absolute after:w-[1px] after:bg-foreground after:top-[38px] after:-bottom-[10px] after:left-[35px]',
                      'last-of-type:after:hidden',
                    )}
                  >
                    {step.status === 'completed' ? (
                      <CheckCircleIcon className="size-6" />
                    ) : (
                      <Circle className="size-6" />
                    )}
                    <span className="flex items-center gap-1">
                      {`${step.label} ${step.id === 'upload' ? `— ${Math.round(upload.progress)}%` : ''}`}
                    </span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );

  return createPortal(statusCard, document.body);
};
